import React from 'react';
import {
  Avatar,
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Grid,
  IconButton,
  Stack,
  Typography,
  alpha,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import PersonOutlineOutlinedIcon from '@mui/icons-material/PersonOutlineOutlined';
import EmailOutlinedIcon from '@mui/icons-material/EmailOutlined';
import PhoneOutlinedIcon from '@mui/icons-material/PhoneOutlined';
import ConfirmationNumberOutlinedIcon from '@mui/icons-material/ConfirmationNumberOutlined';
import CalendarMonthOutlinedIcon from '@mui/icons-material/CalendarMonthOutlined';
import PeopleAltOutlinedIcon from '@mui/icons-material/PeopleAltOutlined';
import PaymentOutlinedIcon from '@mui/icons-material/PaymentOutlined';
import EditNoteOutlinedIcon from '@mui/icons-material/EditNoteOutlined';
import dayjs from 'dayjs';

const getStatusColor = (status) => {
  const s = String(status || '').toUpperCase();
  if (s === 'PENDING_FOR_APPROVAL') return '#d97706';
  if (s === 'VERIFY') return '#2563eb';
  if (s === 'NEED_TO_PAYMENT') return '#7c3aed';
  if (s === 'CONFIRM') return '#059669';
  if (s === 'COMPLETED') return '#0d9488';
  if (s === 'CANCELLED') return '#e11d48';
  return '#64748b';
};

const getPaymentBadge = (status) => {
  const s = String(status || '').toUpperCase();
  if (s === 'PAID' || s === 'SUCCESS') {
    return { color: '#059669', bg: alpha('#059669', 0.12), label: 'PAID' };
  }
  if (s === 'PENDING') {
    return { color: '#d97706', bg: alpha('#d97706', 0.12), label: 'PENDING' };
  }
  return { color: '#dc2626', bg: alpha('#dc2626', 0.12), label: s || 'FAILED' };
};

const OrderDetailsModal = ({
  open,
  handleClose,
  order,
  onOpenStatusModal,
}) => {
  if (!order) return null;

  const orderId = order.id || order._id || order.orderId;
  const status = String(order.orderStatus || order.status || 'PENDING_FOR_APPROVAL').toUpperCase();
  const paymentStatus = String(order.paymentStatus || 'PENDING').toUpperCase();
  const paymentInfo = getPaymentBadge(paymentStatus);

  const expTitle = order.experienceTitle || order.experience?.title || order.title || 'Curated Experience';
  const expCategory = order.experienceCategory || order.experience?.category || order.category || 'General';
  const expLocation = order.experienceLocation || order.experience?.location || order.location || '—';
  const expImage = order.experienceImage || order.experience?.imageUrl || order.imageUrl || '';

  const customerName = order.userName || order.customerName || order.user?.name || 'Customer';
  const customerEmail = order.userEmail || order.customerEmail || order.user?.email || '—';
  const customerPhone = order.userPhone || order.customerPhone || order.user?.phone || '—';
  const userId = order.userId || order.user?.id || '—';

  const bookingDate = order.bookingDate || order.slotDate || order.date || order.createdAt;
  const slotsBooked = order.slotsBooked || order.slots || order.quantity || 1;
  const totalAmount = order.totalAmount ?? order.amount ?? order.price ?? 0;
  const paymentMode = order.paymentMode || order.paymentMethod || 'WALLET';
  const adminRemarks = order.adminRemarks || order.remarks || 'No remarks recorded yet.';
  const createdAt = order.createdAt ? dayjs(order.createdAt).format('DD MMM YYYY, hh:mm A') : '—';

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: '24px',
          boxShadow: '0 24px 60px rgba(15, 23, 42, 0.2)',
          overflow: 'hidden',
        },
      }}
    >
      <DialogTitle
        sx={{
          m: 0,
          p: 3,
          background: 'linear-gradient(135deg, #0c3483 0%, #11409B 50%, #1e40af 100%)',
          color: '#fff',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
        }}
      >
        <Box>
          <Typography variant="caption" sx={{ letterSpacing: '0.08em', opacity: 0.85, fontWeight: 700 }}>
            EXPERIENCE BOOKING DETAILS
          </Typography>
          <Typography variant="h5" sx={{ fontWeight: 800, letterSpacing: '-0.02em', mt: 0.3 }}>
            Order #{orderId}
          </Typography>
        </Box>
        <IconButton
          onClick={handleClose}
          sx={{
            color: '#fff',
            bgcolor: alpha('#fff', 0.12),
            '&:hover': { bgcolor: alpha('#fff', 0.24) },
          }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ p: 3.5, bgcolor: '#f8fafc' }}>
        {/* Top Badges & Timestamps */}
        <Stack direction="row" justifyContent="space-between" alignItems="center" flexWrap="wrap" gap={2} mb={3}>
          <Stack direction="row" spacing={1.5} alignItems="center">
            <Chip
              label={`Order Status: ${status}`}
              sx={{
                fontWeight: 800,
                bgcolor: alpha(getStatusColor(status), 0.12),
                color: getStatusColor(status),
                border: `1px solid ${alpha(getStatusColor(status), 0.3)}`,
              }}
            />
            <Chip
              label={`Payment: ${paymentInfo.label}`}
              sx={{
                fontWeight: 800,
                bgcolor: paymentInfo.bg,
                color: paymentInfo.color,
                border: `1px solid ${alpha(paymentInfo.color, 0.3)}`,
              }}
            />
          </Stack>
          <Typography variant="caption" sx={{ color: '#64748b', fontWeight: 600 }}>
            Created: {createdAt}
          </Typography>
        </Stack>

        <Grid container spacing={3}>
          {/* Experience Card Details */}
          <Grid item xs={12} sm={6}>
            <Box
              sx={{
                p: 2.5,
                bgcolor: '#fff',
                borderRadius: '16px',
                border: '1px solid rgba(0,0,0,0.06)',
                height: '100%',
              }}
            >
              <Typography variant="caption" sx={{ color: '#94a3b8', fontWeight: 700, letterSpacing: '0.05em' }}>
                BOOKED EXPERIENCE
              </Typography>
              <Stack direction="row" spacing={2} sx={{ mt: 1.5, mb: 2 }}>
                <Avatar
                  variant="rounded"
                  src={expImage}
                  sx={{ width: 64, height: 64, borderRadius: '12px' }}
                >
                  {expTitle.charAt(0)}
                </Avatar>
                <Box>
                  <Typography variant="body1" sx={{ fontWeight: 800, color: '#0f172a' }}>
                    {expTitle}
                  </Typography>
                  <Chip
                    label={expCategory}
                    size="small"
                    sx={{
                      height: 20,
                      fontSize: '0.68rem',
                      fontWeight: 700,
                      bgcolor: alpha('#11409B', 0.08),
                      color: '#11409B',
                      mt: 0.5,
                    }}
                  />
                  <Typography variant="caption" sx={{ color: '#64748b', display: 'block', mt: 0.5 }}>
                    📍 {expLocation}
                  </Typography>
                </Box>
              </Stack>

              <Divider sx={{ my: 1.5 }} />

              <Stack spacing={1}>
                <Stack direction="row" justifyContent="space-between">
                  <Typography variant="caption" sx={{ color: '#64748b' }}>
                    Booking Date:
                  </Typography>
                  <Typography variant="caption" sx={{ fontWeight: 700, color: '#0f172a' }}>
                    {bookingDate ? dayjs(bookingDate).format('DD MMM YYYY') : '—'}
                  </Typography>
                </Stack>
                <Stack direction="row" justifyContent="space-between">
                  <Typography variant="caption" sx={{ color: '#64748b' }}>
                    Reserved Capacity:
                  </Typography>
                  <Typography variant="caption" sx={{ fontWeight: 700, color: '#0f172a' }}>
                    {slotsBooked} {slotsBooked === 1 ? 'Slot' : 'Slots'}
                  </Typography>
                </Stack>
                <Stack direction="row" justifyContent="space-between">
                  <Typography variant="caption" sx={{ color: '#64748b' }}>
                    Total Booking Price:
                  </Typography>
                  <Typography variant="body2" sx={{ fontWeight: 800, color: '#16a34a' }}>
                    ₹{Number(totalAmount).toLocaleString('en-IN')}
                  </Typography>
                </Stack>
              </Stack>
            </Box>
          </Grid>

          {/* Customer Details */}
          <Grid item xs={12} sm={6}>
            <Box
              sx={{
                p: 2.5,
                bgcolor: '#fff',
                borderRadius: '16px',
                border: '1px solid rgba(0,0,0,0.06)',
                height: '100%',
              }}
            >
              <Typography variant="caption" sx={{ color: '#94a3b8', fontWeight: 700, letterSpacing: '0.05em' }}>
                CUSTOMER INFORMATION
              </Typography>

              <Stack spacing={2} sx={{ mt: 2 }}>
                <Stack direction="row" spacing={1.5} alignItems="center">
                  <Avatar sx={{ bgcolor: alpha('#11409B', 0.1), color: '#11409B', width: 36, height: 36 }}>
                    <PersonOutlineOutlinedIcon fontSize="small" />
                  </Avatar>
                  <Box>
                    <Typography variant="caption" sx={{ color: '#64748b', display: 'block' }}>
                      Customer Name
                    </Typography>
                    <Typography variant="body2" sx={{ fontWeight: 700, color: '#0f172a' }}>
                      {customerName}
                    </Typography>
                  </Box>
                </Stack>

                <Stack direction="row" spacing={1.5} alignItems="center">
                  <Avatar sx={{ bgcolor: alpha('#2563eb', 0.1), color: '#2563eb', width: 36, height: 36 }}>
                    <EmailOutlinedIcon fontSize="small" />
                  </Avatar>
                  <Box>
                    <Typography variant="caption" sx={{ color: '#64748b', display: 'block' }}>
                      Email Address
                    </Typography>
                    <Typography variant="body2" sx={{ fontWeight: 600, color: '#0f172a' }}>
                      {customerEmail}
                    </Typography>
                  </Box>
                </Stack>

                <Stack direction="row" spacing={1.5} alignItems="center">
                  <Avatar sx={{ bgcolor: alpha('#16a34a', 0.1), color: '#16a34a', width: 36, height: 36 }}>
                    <PhoneOutlinedIcon fontSize="small" />
                  </Avatar>
                  <Box>
                    <Typography variant="caption" sx={{ color: '#64748b', display: 'block' }}>
                      Phone / Mobile
                    </Typography>
                    <Typography variant="body2" sx={{ fontWeight: 600, color: '#0f172a' }}>
                      {customerPhone}
                    </Typography>
                  </Box>
                </Stack>

                <Stack direction="row" spacing={1.5} alignItems="center">
                  <Avatar sx={{ bgcolor: alpha('#7c3aed', 0.1), color: '#7c3aed', width: 36, height: 36 }}>
                    <PaymentOutlinedIcon fontSize="small" />
                  </Avatar>
                  <Box>
                    <Typography variant="caption" sx={{ color: '#64748b', display: 'block' }}>
                      Payment Method
                    </Typography>
                    <Typography variant="body2" sx={{ fontWeight: 600, color: '#0f172a' }}>
                      {paymentMode}
                    </Typography>
                  </Box>
                </Stack>
              </Stack>
            </Box>
          </Grid>

          {/* Admin Remarks / Audit Trail */}
          <Grid item xs={12}>
            <Box
              sx={{
                p: 2.5,
                bgcolor: '#fff',
                borderRadius: '16px',
                border: '1px solid rgba(0,0,0,0.06)',
              }}
            >
              <Typography variant="caption" sx={{ color: '#94a3b8', fontWeight: 700, letterSpacing: '0.05em' }}>
                ADMIN REMARKS & LOGS
              </Typography>
              <Typography
                variant="body2"
                sx={{
                  mt: 1,
                  color: '#475569',
                  bgcolor: '#f8fafc',
                  p: 2,
                  borderRadius: '12px',
                  border: '1px solid rgba(0,0,0,0.04)',
                }}
              >
                {adminRemarks}
              </Typography>
            </Box>
          </Grid>
        </Grid>
      </DialogContent>

      <DialogActions sx={{ p: 2.5, bgcolor: '#fff', borderTop: '1px solid rgba(0,0,0,0.06)' }}>
        <Button
          onClick={handleClose}
          sx={{
            borderRadius: '12px',
            textTransform: 'none',
            fontWeight: 600,
            color: '#64748b',
          }}
        >
          Close
        </Button>
        {onOpenStatusModal && (
          <Button
            onClick={() => {
              handleClose();
              onOpenStatusModal(order);
            }}
            variant="contained"
            startIcon={<EditNoteOutlinedIcon />}
            sx={{
              borderRadius: '12px',
              textTransform: 'none',
              fontWeight: 700,
              background: 'linear-gradient(135deg, #0c3483 0%, #11409B 100%)',
            }}
          >
            Manage Status & Payment
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default OrderDetailsModal;
